import { useCallback, useEffect, useMemo, useState } from "react";

export function normalizeCatalogsIndex(raw) {
  const idx = {};
  for (const [k, v] of Object.entries(raw || {})) {
    if (!v || typeof v !== "object") continue;
    const id = Number(v.id ?? k);
    if (!Number.isFinite(id)) continue;
    idx[id] = {
      id,
      parent_id: v.parent_id != null ? Number(v.parent_id) : null,
      name: (v.name && String(v.name).trim()) || "—",
    };
  }
  return idx;
}

export function buildCatalogsWithAssembly(catalogIds, catalogsIndex) {
  const has = new Set();
  for (const cid of catalogIds) {
    if (cid == null) continue;
    let id = Number(cid);
    const guard = new Set();
    while (id && !guard.has(id)) {
      guard.add(id);
      has.add(id);
      id = catalogsIndex[id]?.parent_id ?? null;
    }
  }
  return has;
}

function collectCatalogSubtreeIds(rootId, catalogsIndex) {
  const out = new Set([rootId]);
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift();
    for (const c of Object.values(catalogsIndex)) {
      if (c.parent_id === id && !out.has(c.id)) {
        out.add(c.id);
        queue.push(c.id);
      }
    }
  }
  return out;
}

export function countGroupsInCatalogSubtree(catalogId, groups, catalogsIndex) {
  const subtree = collectCatalogSubtreeIds(catalogId, catalogsIndex);
  return groups.filter((g) => g.catalog_id != null && subtree.has(g.catalog_id)).length;
}

export function filterGroupForCatalogView(group, currentCatalogId) {
  if (currentCatalogId == null) return group.catalog_id == null;
  return group.catalog_id === currentCatalogId;
}

/** Сводка позиций Ozon по SKU/offer для режима «по производителям». */
export function groupOzonProductsForCatalog(lines) {
  const byKey = new Map();
  for (const ln of lines || []) {
    const mapKey =
      ln.sku != null ? `sku:${ln.sku}` : `offer:${(ln.offer_id || "").trim() || "?"}`;
    if (!byKey.has(mapKey)) {
      byKey.set(mapKey, {
        key: mapKey,
        sku: ln.sku ?? null,
        offer_id: (ln.offer_id || "").trim(),
        title: ln.title || "",
        catalog_id: ln.catalog_id ?? null,
        need: 0,
        done: 0,
      });
    }
    const g = byKey.get(mapKey);
    const need = Number(ln.line_qty) || 0;
    const done = Math.min(Number(ln.scanned_qty) || 0, need);
    g.need += need;
    g.done += done;
    if (ln.catalog_id != null) g.catalog_id = ln.catalog_id;
    if (ln.title && (!g.title || g.title.startsWith("SKU "))) g.title = ln.title;
  }
  return Array.from(byKey.values()).sort((a, b) =>
    (a.title || a.key).localeCompare(b.title || b.key, "ru"),
  );
}

/** Сводка позиций Маркета по offer для режима «по производителям». */
export function groupYmProductsForCatalog(lines) {
  const byKey = new Map();
  for (const ln of lines || []) {
    const offer = (ln.offer_id || "").trim() || "?";
    const mapKey = `offer:${offer}`;
    if (!byKey.has(mapKey)) {
      byKey.set(mapKey, {
        key: mapKey,
        offer_id: offer,
        title: ln.title || "",
        catalog_id: ln.catalog_id ?? null,
        need: 0,
        done: 0,
      });
    }
    const g = byKey.get(mapKey);
    const qty = Number(ln.line_qty) || 0;
    const need = ln.marking ? qty : 0;
    const done = Math.min(Number(ln.scanned_qty) || 0, need);
    g.need += need;
    g.done += done;
    if (ln.catalog_id != null) g.catalog_id = ln.catalog_id;
    if (ln.title) g.title = ln.title;
  }
  return Array.from(byKey.values()).sort((a, b) =>
    (a.title || a.offer_id).localeCompare(b.title || b.offer_id, "ru"),
  );
}

export function useManufacturerCatalogView(groups, catalogsIndex) {
  const [groupByManufacturer, setGroupByManufacturer] = useState(false);
  const [catalogStack, setCatalogStack] = useState([]);

  useEffect(() => {
    if (!groupByManufacturer) setCatalogStack([]);
  }, [groupByManufacturer]);

  const currentCatalogId =
    catalogStack.length > 0 ? catalogStack[catalogStack.length - 1].id : null;

  const catalogsWithAssembly = useMemo(() => {
    const ids = groups.map((g) => g.catalog_id).filter((id) => id != null);
    return buildCatalogsWithAssembly(ids, catalogsIndex);
  }, [groups, catalogsIndex]);

  const visibleCatalogs = useMemo(() => {
    if (!groupByManufacturer) return [];
    return Object.values(catalogsIndex)
      .filter(
        (c) => c.parent_id === currentCatalogId && catalogsWithAssembly.has(c.id),
      )
      .sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [groupByManufacturer, catalogsIndex, currentCatalogId, catalogsWithAssembly]);

  const sortedIncomplete = useMemo(() => {
    const incomplete = groups.filter((g) => g.need > 0 && g.done < g.need);
    return incomplete.sort((a, b) =>
      (a.title || a.key || "").localeCompare(b.title || b.key || "", "ru"),
    );
  }, [groups]);

  const sortedComplete = useMemo(() => {
    return groups
      .filter((g) => g.need > 0 && g.done >= g.need)
      .sort((a, b) => (a.title || a.key || "").localeCompare(b.title || b.key || "", "ru"));
  }, [groups]);

  const filteredIncomplete = useMemo(() => {
    if (!groupByManufacturer) return sortedIncomplete;
    return sortedIncomplete.filter((g) => filterGroupForCatalogView(g, currentCatalogId));
  }, [groupByManufacturer, sortedIncomplete, currentCatalogId]);

  const filteredComplete = useMemo(() => {
    if (!groupByManufacturer) return sortedComplete;
    return sortedComplete.filter((g) => filterGroupForCatalogView(g, currentCatalogId));
  }, [groupByManufacturer, sortedComplete, currentCatalogId]);

  const handleEnterCatalog = useCallback((catalog) => {
    setCatalogStack((prev) => [...prev, { id: catalog.id, name: catalog.name }]);
  }, []);

  const handleCatalogBreadcrumbClick = useCallback((index) => {
    if (index <= 0) {
      setCatalogStack([]);
      return;
    }
    setCatalogStack((prev) => prev.slice(0, index));
  }, []);

  return {
    groupByManufacturer,
    setGroupByManufacturer,
    catalogStack,
    currentCatalogId,
    visibleCatalogs,
    filteredIncomplete,
    filteredComplete,
    sortedIncomplete,
    sortedComplete,
    handleEnterCatalog,
    handleCatalogBreadcrumbClick,
  };
}
